import React, { useState, useEffect, useRef, useMemo } from "react";
import { Icon, Box, TextField, Button, Checkbox } from "@mui/material";
import "./SiteTracker.css";
import { useSelector, useDispatch } from "react-redux";
import Translate from "../Translate/Translate";
import { selectZones } from "../../redux/reducers/zones";
import MapComponent from "../SiteTrackerMap/SiteTrackerMap";
import WorkersAnalytics from "../Analytics/WorkersAnalytics/WorkersAnalytics";
import {updateIsWorkersAnalyticsPageOpen} from "../../redux/reducers/fieldMembers";

const SiteTracker = () => {
  const selectAllOptions = process.env.REACT_APP_ALL_SELECT_OPTION;
  const tableData = useSelector((state) => state.fieldMembers.data);
  const isWorkersAnalyticsPageOpen = useSelector((state) => state.fieldMembers.isWorkersAnalyticsPageOpen)
  const zones = useSelector((state) => state.zones);
  const [showFilters, setShowFilters] = useState({
    zones: false,
  });
  const [checkedWorkers, setCheckedWorkers] = useState({});
  const [workerColors, setWorkerColors] = useState({});
  const sitesForMap = useSelector((state) => state.markerCoordinates.data);
  const workerAssignedSitesCount = useSelector(
    (state) => state.markerCoordinates.workerAssignedSitesCount
  );
  const [sortKey, setSortKey] = useState(null);
  const [sortDirections, setSortDirections] = useState({
    first_name: "asc",
    active: "asc",
    completed: "asc",
  });

  const dispatch = useDispatch();

  const dropdownRefs = useRef([]);

  const getRandomColor = () =>
    "#" + Math.floor(Math.random() * 16777215).toString(16);

  const toggleDropdown = (dropdown) => {
    console.log("toggleDropdown ", dropdown);
    setShowFilters({
      ...showFilters,
      zones: false,
      [dropdown]: !showFilters[dropdown],
    });
  };

  const handleCloseDropdown = (dropdown, index) => {
    return (e) => {
      if (dropdownRefs.current[index].contains(e.relatedTarget)) {
        return;
      }

      setShowFilters({
        ...showFilters,
        [dropdown]: false,
      });
    };
  };

  const handleZoneClick = (zone) => {
    if (zone === selectAllOptions) {
      const newStatus = zones.selected[selectAllOptions];
      const undefinedRemoved = Object.fromEntries(
        Object.entries(zones.selected).filter(([key]) => key)
      );

      const toggledSelected = Object.fromEntries(
        Object.entries(undefinedRemoved).map(([key, value]) => [
          key,
          !newStatus,
        ])
      );
      dispatch(selectZones(toggledSelected));
    } else {
      const tempObj = {};
      tempObj[zone] = true;

      if (zones.selected[zone] === null || zones.selected[zone] === true) {
        tempObj[zone] = false;
      }

      dispatch(selectZones(tempObj));
    }
  };

  const handleCheckboxChange = (employee_id) => {
    setCheckedWorkers((prev) => {
      const newChecked = !prev[employee_id];

      // Update color mapping
      setWorkerColors((prevColors) => {
        if (newChecked) {
          return {
            ...prevColors,
            [employee_id]: getRandomColor(),
          };
        } else {
          // Remove color when unchecked
          const { [employee_id]: _, ...rest } = prevColors;
          return rest;
        }
      });

      return {
        ...prev,
        [employee_id]: newChecked,
      };
    });
  };

  const getCompletedSites = (row) => {
    return workerAssignedSitesCount[row.employee_id] !== undefined
      ? workerAssignedSitesCount[row.employee_id] - row.num_active_sites
      : row.num_active_sites;
  };

  const sortedTableData = useMemo(() => {
    // clone & filter rows
    const data = tableData.filter((d) => zones.selected[d.zone]);

    if (!sortKey) return data;

    return [...data].sort((a, b) => {
      let aVal, bVal;

      if (sortKey === "first_name") {
        aVal = a.first_name.toLowerCase();
        bVal = b.first_name.toLowerCase();
      } else if (sortKey === "active") {
        aVal = a.num_active_sites;
        bVal = b.num_active_sites;
      } else if (sortKey === "completed") {
        aVal = getCompletedSites(a);
        bVal = getCompletedSites(b);
      }

      if (aVal < bVal) return sortDirections[sortKey] === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirections[sortKey] === "asc" ? 1 : -1;
      return 0;
    });
  }, [tableData, sortKey, sortDirections, zones.selected]);

  const toggleSort = (key) => {
    setSortKey(key);

    setSortDirections((prev) => ({
      first_name:
        key === "first_name"
          ? prev.first_name === "asc"
            ? "desc"
            : "asc"
          : "asc",
      active:
        key === "active" ? (prev.active === "asc" ? "desc" : "asc") : "asc",
      completed:
        key === "completed"
          ? prev.completed === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  const handleClick = () => {
    dispatch(updateIsWorkersAnalyticsPageOpen(!isWorkersAnalyticsPageOpen));
  };

  return (
    <div className="tracker-box-container">
      <div className="left-panel">
        {/* filter on left panel */}
        <div className="left-panel-filter-container">
          <div className="open-analytics" onClick={handleClick}>
              <Icon
                title="Workers Analytics"
                baseClassName="fa"
                className="fa-solid fa-chart-line sidebar-container-row"
                fontSize="medium"
              />
          </div>
          <div
            className="filters-dropdown-container-box"
            onBlur={handleCloseDropdown("zones", 5)}
            ref={(el) => (dropdownRefs.current[5] = el)} // Set a ref to the first dropdown
            tabIndex={16}
          >
            <Button
              onClick={() =>
                // hide other dropdowns
                toggleDropdown("zones")
              }
              variant="contained"
              style={{
                borderRadius: "0.75rem",
                background: "var(--bg-color)",
                color: "var(--text-color)",
                padding: "1rem",
                width: "8rem",
                display: "flex",
                justifyContent: "space-between",
              }}
              className="filters-button"
            >
              <Icon
                title="Filter"
                baseClassName="fas"
                className="fa-regular fa-filter container-icon"
                fontSize="small"
              />

              <p className="filters-button-p">
                <Translate text="Zones" />
              </p>

              <Icon
                title="Expand"
                baseClassName="fas"
                className="fa-regular fa-caret-down"
                fontSize="small"
              />
            </Button>

            {showFilters.zones && (
              <div className="status-select-dropdown">
                {zones.data.map((zone) => {
                  return (
                    <div
                      key={"zone-" + zone.Zone}
                      className="site-info-status-select-sub-container cursor-pointer"
                      style={{ color: zone.color }}
                      onClick={() => {
                        handleZoneClick(zone.Zone);
                      }}
                    >
                      <Checkbox
                        onChange={(evt) => handleZoneClick(zone.Zone)}
                        checked={zones.selected[zone.Zone] === true}
                      />

                      <p className="m-0" style={{ flexGrow: 1 }}>
                        <Translate text={zone.Zone} />
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* list of workers according to selected zones */}
        <div>
          {/* HEADER ROW - fixed */}
          <div className="left-panel-table-header-subcontainer">
            <Checkbox disabled />

            {/* Name Column */}
            <div
              style={{
                width: "40%",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                cursor: "pointer",
              }}
              onClick={() => toggleSort("first_name")}
            >
              <span>{sortDirections.first_name === "asc" ? "▲" : "▼"}</span>
              <Translate text="Name" />
            </div>

            {/* Active Sites Column */}
            <div
              className="header-icon"
              onClick={() => toggleSort("active")}
              style={{ cursor: "pointer" }}
            >
              <span>{sortDirections.active === "asc" ? "▲" : "▼"}</span>
              <Icon className="fas fa-play-circle orange-icon" title="Active" />
            </div>

            {/* Completed Sites Column */}
            <div
              className="header-icon"
              onClick={() => toggleSort("completed")}
              style={{ cursor: "pointer" }}
            >
              <span>{sortDirections.completed === "asc" ? "▲" : "▼"}</span>
              <Icon
                className="fas fa-check-circle green-icon"
                title="Completed"
              />
            </div>
          </div>

          {/* SCROLLABLE DATA ROWS */}
          <div className="left-panel-worker-list-container">
            {sortedTableData.map((data, index) => {
              if (!zones.selected[data.zone]) return null;

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #ddd",
                    padding: "0.25rem 1rem",
                  }}
                >
                  <Checkbox
                    checked={!!checkedWorkers[data.employee_id]}
                    onChange={() => handleCheckboxChange(data.employee_id)}
                  />

                  <div
                    style={{
                      width: "40%",
                      display: "flex",
                      alignItems: "center",
                      color: workerColors[data.employee_id] || "grey",
                    }}
                  >
                    <Translate
                      text={`${data.first_name} --${data.employee_id}`}
                    />
                  </div>

                  <div style={{ width: "15%", textAlign: "right" }}>
                    {data.num_active_sites}
                  </div>

                  <div style={{ width: "15%", textAlign: "right" }}>
                    {getCompletedSites(data)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="right-panel">
        <div>
          <MapComponent
            mapMarkers={sitesForMap}
            selectedWorkers={checkedWorkers}
            workerColors={workerColors}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteTracker;
